import { useCallback, useEffect, useRef, useState } from 'react'
import { ComicImage } from './types'
import { imageUrl } from './format'
import { MAX_IMAGES, rejectReason, uploadImage } from './uploads'

export interface ImageItem {
  id: string
  /** Stored key, once uploaded (or for images already saved) */
  key?: string
  file?: File
  preview: string
  name: string
  size?: number
  status: 'waiting' | 'uploading' | 'done' | 'failed'
  progress: number
  error?: string
  altText: string
}

let nextId = 0
const newId = () => `img-${++nextId}`

export function itemsFromImages(images: ComicImage[]): ImageItem[] {
  return images.map((img) => ({
    id: newId(),
    key: img.key,
    preview: imageUrl(img.key),
    name: img.key.split('/').pop() ?? 'image',
    status: 'done',
    progress: 100,
    altText: img.altText,
  }))
}

const PARALLEL = 2

/**
 * A list of images that upload as soon as they're added, two at a time.
 * Files of the wrong type or size, or past the limit, are turned away with a reason.
 */
export function useImageUploads(initial: ComicImage[] = [], max = MAX_IMAGES) {
  const [items, setItems] = useState<ImageItem[]>(() => itemsFromImages(initial))
  const [rejected, setRejected] = useState<Array<{ name: string; reason: string }>>([])
  const controllers = useRef(new Map<string, AbortController>())

  const patch = useCallback((id: string, changes: Partial<ImageItem>) => {
    setItems((list) => list.map((it) => (it.id === id ? { ...it, ...changes } : it)))
  }, [])

  // Start waiting uploads while fewer than PARALLEL are running
  useEffect(() => {
    const running = items.filter((it) => it.status === 'uploading').length
    const next = items.filter((it) => it.status === 'waiting').slice(0, Math.max(0, PARALLEL - running))
    for (const it of next) {
      const controller = new AbortController()
      controllers.current.set(it.id, controller)
      patch(it.id, { status: 'uploading', progress: 0, error: undefined })
      uploadImage(it.file!, (progress) => patch(it.id, { progress }), controller.signal)
        .then((key) => patch(it.id, { key, status: 'done', progress: 100 }))
        .catch((error) => {
          if (error?.name === 'AbortError') return
          patch(it.id, { status: 'failed', error: error?.message ?? 'The upload failed.' })
        })
        .finally(() => controllers.current.delete(it.id))
    }
  }, [items, patch])

  useEffect(() => {
    const map = controllers.current
    return () => map.forEach((c) => c.abort())
  }, [])

  const current = useRef(items)
  current.current = items

  const add = useCallback(
    (files: File[]) => {
      const turnedAway: Array<{ name: string; reason: string }> = []
      const room = max - current.current.length
      const accepted: ImageItem[] = []
      for (const file of files) {
        const reason = rejectReason(file)
        if (reason) turnedAway.push({ name: file.name, reason })
        else if (accepted.length >= room) turnedAway.push({ name: file.name, reason: max === 1 ? 'There’s room for one image. Remove the current one first.' : `A comic can have up to ${max} images.` })
        else
          accepted.push({
            id: newId(),
            file,
            preview: URL.createObjectURL(file),
            name: file.name,
            size: file.size,
            status: 'waiting',
            progress: 0,
            altText: '',
          })
      }
      setItems((list) => [...list, ...accepted])
      setRejected(turnedAway)
    },
    [max]
  )

  const remove = useCallback((id: string) => {
    controllers.current.get(id)?.abort()
    setItems((list) => list.filter((it) => it.id !== id))
  }, [])

  const retry = useCallback((id: string) => patch(id, { status: 'waiting', progress: 0, error: undefined }), [patch])

  const move = useCallback((from: number, to: number) => {
    setItems((list) => {
      if (to < 0 || to >= list.length) return list
      const copy = list.slice()
      const [it] = copy.splice(from, 1)
      copy.splice(to, 0, it)
      return copy
    })
  }, [])

  const setAlt = useCallback((id: string, altText: string) => patch(id, { altText }), [patch])

  const reset = useCallback((images: ComicImage[]) => {
    setItems(itemsFromImages(images))
    setRejected([])
  }, [])

  const busy = items.some((it) => it.status === 'waiting' || it.status === 'uploading')
  const failed = items.some((it) => it.status === 'failed')
  const saved: ComicImage[] = items.filter((it) => it.key).map((it) => ({ key: it.key!, altText: it.altText.trim() }))

  return { items, rejected, add, remove, retry, move, setAlt, reset, busy, failed, saved, clearRejected: () => setRejected([]) }
}

export type ImageUploads = ReturnType<typeof useImageUploads>
